import React, { useCallback, useEffect } from "react"
import uuidv4 from "uuid/v4"
import { LOOK_UP_CONTACT_ZIP_CODE } from "../wizard"
import { ICoreProps } from "./registration_form_component"
import { isBlank, formatPhoneNumber } from "./helpers"
import { FormHelpers } from "./form_helpers"
import {
  IRegistrationForm,
  IContactWithUUID,
  IPlanParentOrganization,
  IContactRef,
  OTHER,
  NOT_APPLICABLE, IContactStore,
  YES, NO,
} from "../data"
import { ContactTypeName } from "../clients/config"
import { EMAIL_FIELD_PATTERN } from "./validation";


interface IContactFillingFormProps extends ICoreProps {
  name: keyof IRegistrationForm
  title: string
  description: string
  default_to_new_contact?: boolean
  default_company?: string
  email_confirmation?: boolean
  required: boolean
  showNotApplicable: boolean
  contactExclusivity?: ContactTypeName[]
  showPlanSelection?: boolean
  selectablePlans?: IPlanParentOrganization[]
  /**
   * If set, the user must select at least one plan/contact from this group.
   * (Checked during validation using data-fv-* attributes.)
   */
  oneRequiredInGroup?: OneRequiredInGroupProps
}

interface OneRequiredInGroupProps {
  name: string
  message?: string
}

/**
 * Architecture notes:
 *
 * All ContactFillingForms have a uuid. This uuid points to an entirely NEW contact in
 * the form.contacts repository, owned by this and only this ContactFillingForm.
 *
 * They also control a reference
 *
 * @param {object} props - React props
 * @returns {object} - the JSX element
 */
export function ContactFillingForm(props: IContactFillingFormProps) {
  const {
    name,
    form,
    dispatch,
  } = props

  const contactRef = (form ? (form[name] as IContactRef) : null)

  // TODO: maybe doesn't need to be a function?
  const getUuid = () => {
    if (contactRef && typeof contactRef["new_uuid"] === "string") {
      return contactRef["new_uuid"]
    } else {
      return null
    }
  }

  useEffect(() => {
    let uuid = getUuid()

    const refUpdates: Partial<IContactRef> = {
      ...contactRef,
    }
    const formUpdates: Partial<IRegistrationForm> = {}
    formUpdates[name] = refUpdates

    // if we don't have a uuid for a new contact, create a new one, unique to this ContactFillingForm
    if (!uuid) {
      uuid = uuidv4()
      // console.log(`setting ${field} new_uuid to ${uuid}`)
      // setFormValue([name, "new_uuid"], uuid)
      refUpdates.new_uuid = uuid
    }

    // default to a new contact by selecting the new_uuid
    if (props.default_to_new_contact && !contactRef?.contact && uuid) {
      // setFormValue([name, "contact"], uuid)
      refUpdates.contact = uuid
    }

    // default the contact's first name
    if (props.default_company) {
      const hasValue = !!form?.contacts?.[uuid]
      if (!hasValue) {
        formUpdates.contacts = {
          ...form?.contacts,
        }
        formUpdates.contacts[uuid] = {
          ...form?.contacts?.[uuid],
          company_name: props.default_company,
        }
        // setFormValue(["contacts", uuid], {
        //   company_name: this.props.default_company,
        // })
      }
    }

    dispatch({
      action: "UPDATE_FORM",
      form: formUpdates,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * List all contacts except the "New Contact" contact associated with this form.
   * Handles contact exclusivity.
   *
   * @param {object} options options object
   * @param {string} options.except optional contact uuid to exclude
   * @returns {Array} array of contacts filtered by options and contact exclusivity
   * @private
   */
  const filteredContactsWithUUIDs = (options: { except?: string } = {}): IContactWithUUID[] => {
    const form = props.form || ({} as IRegistrationForm)
    const contacts = form.contacts || ({} as IContactStore) // contacts = { uuid1 => obj, uuid2 => obj }

    const excludedUuids: string[] = []

    if (options.except) {
      excludedUuids.push(options.except)
    }

    if (props.contactExclusivity) {
      for (const otherName of props.contactExclusivity) {
        if (otherName === props.name) {
          continue
        }

        const otherContactRef: IContactRef = (form[otherName] as IContactRef | undefined) || {}

        const otherValue = otherContactRef.contact
        if (typeof otherValue === "string" && otherValue !== NOT_APPLICABLE && otherValue !== "") {
          excludedUuids.push(otherValue)
        }
      }
    }

    // remove any that don't have certain properties
    const contactsWithData: IContactWithUUID[] = []
    // the filter function type signature is wrong
    for (const [uuid, contact] of Object.entries(contacts)) {
      if (excludedUuids.includes(uuid)) {
        continue
      }

      if (contact && contact.first_name && contact.last_name && contact.email) {
        contactsWithData.push({
          ...contact,
          uuid: uuid,
        })
      }
    }

    return contactsWithData
  }

  const uuid = getUuid()

  if (!uuid) {
    return null
  }

  const {
    title,
    description,
    showPlanSelection,
    contactExclusivity,
  } = props

  const isNewContact = contactRef?.contact === uuid

  let planNotApplicable = false
  if ( showPlanSelection ) {
    const planCodes = contactRef?.plan_codes

    if ( Array.isArray(planCodes) && planCodes.length >= 1 && !planCodes.find(pc => pc.code !== NOT_APPLICABLE) ) {
      planNotApplicable = true
    }
  }

  const excludedEmails: string[] = []
  if ( contactExclusivity && form && form.contacts ) {
    for ( const excludedName of contactExclusivity ) {
      if ( excludedName === name ) {
        // don't exclude this contact form's current email
        continue
      }

      const ref = form[excludedName] as IContactRef | undefined
      if (!ref) {
        continue
      }

      const uuid = ref.contact
      if (!uuid) {
        continue
      }

      const contact = form.contacts[uuid]
      if (!contact) {
        continue
      }

      const email = contact.email
      if (!email) {
        continue
      }

      excludedEmails.push(email)
    }
  }

  return (
    <div className={`panel panel-default contact ${name}`} data-uuid={uuid}>
      <div className="panel-heading">
        <h1 className="panel-title">{title}</h1>
        <p className="panel-description">{description}</p>
      </div>

      <div className="panel-body form-horizontal contact-area">
        {showPlanSelection && (
          <PlanMultiSelect
            {...props}
            selectablePlans={props.selectablePlans || []}
            oneRequiredPlanGroup={props.oneRequiredInGroup}
          />
        )}

        {planNotApplicable || (
          <div>
            <SelectContact
              {...props}
              uuid={uuid}
              showNotApplicable={showPlanSelection ? false : props.showNotApplicable}
              contacts={filteredContactsWithUUIDs({except: uuid})}
            />

            {isNewContact && (
              <ContactFormFields
                {...props}
                excluded_addresses={excludedEmails}
                uuid={uuid}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface ISelectContactProps extends ICoreProps {
  name: keyof IRegistrationForm
  uuid: string
  required: boolean
  showNotApplicable: boolean
  contacts: IContactWithUUID[]
}

function SelectContact(props: ISelectContactProps): React.ReactNode {
  const {
    form,
    name,
    uuid,
    contacts,
    onInputChange,
    required,
    showNotApplicable,
  } = props

  const contactRef: IContactRef | null = form && form[name] ? (form[name] as IContactRef) : null
  // const contactStore = (form && form.contacts) ?? ({} as IContactStore)
  // const contact = contactRef && contactRef.contact ? contactStore[contactRef.contact] : null

  return (
    <div className="form-group">
      <label className={`col-sm-3 control-label${required ? " required" : ""}`} htmlFor={`${name}_contact`}>
        Select a Contact
      </label>
      <div className="col-sm-9">
        <select
          name={`${name}[contact]`}
          id={`${name}_contact`}
          className="form-control contact-select"
          onChange={onInputChange}
          value={contactRef?.contact ?? ""}
          data-fv-notempty-message="Select a contact"
          required={required}
        >
          <option value="" />
          <option value={uuid}>New Contact</option>
          {(() => {
            return contacts.map((contact) => {
              return (
                <option value={contact.uuid} key={contact.uuid}>
                  {[contact.first_name, contact.last_name].join(" ")}
                </option>
              )
            })
          })()}
          {showNotApplicable && <option value={NOT_APPLICABLE}>Not Applicable</option>}
        </select>
      </div>
    </div>
  )
}

export interface ExampleContactFillingFormProps extends ICoreProps {
}

export function ExampleContactFillingForm(props: ExampleContactFillingFormProps) {
  return (
    <div className="panel panel-default contact" data-uuid="example">
      <div className="panel-heading">
        <h1 className="panel-title">Example New Contact Form</h1>
        <p className="panel-description">Note that for each new contact, you will be asked to provide the following information:</p>
      </div>
      <div className="panel-body form-horizontal contact-area">
        <ContactFormFields
          {...props}
          name="example"
          uuid="example"
          required={false}
          excluded_addresses={[]}
        />
      </div>
    </div>
  )
}

interface IContactFormFieldsProps extends ICoreProps {
  name: keyof IRegistrationForm
  uuid: string
  required: boolean
  email_confirmation?: boolean
  excluded_addresses: string[]
}

function ContactFormFields(props: IContactFormFieldsProps): React.ReactNode {
  const {
    form,
    name,
    uuid,
    required,
    email_confirmation,
    dispatch,
    onInputChange,
  } = props

  /**
   * Force a lookup of the final zip code.
   */
  const zipCodeBlurred = useCallback(() => {
    if (uuid) {
      dispatch({
        action: LOOK_UP_CONTACT_ZIP_CODE,
        path: ["contacts", uuid],
      })
    }
  }, [uuid, dispatch])

  const contactRef: IContactRef | null = form && form[name] ? (form[name] as IContactRef) : null
  const contactStore = (form && form.contacts) ?? ({} as IContactStore)
  const contact = contactRef && contactRef.contact ? contactStore[contactRef.contact] : null

  const mainInputClass = "col-sm-9"
  const labelClass = "col-sm-3 control-label"
  const requiredLabel = `col-sm-3 control-label${required ? " required" : ""}`

  let shouldDisplayFullAddress = true
  if (contact && typeof contact === "object") {
    const { city, zip, state, country_code } = contact

    if (
      isBlank(city) &&
      isBlank(state) &&
      isBlank(country_code) &&
      (isBlank(zip) || (typeof zip === "string" && zip.length < 5))
    ) {
      shouldDisplayFullAddress = false
    }
  } else if (!contact) {
    shouldDisplayFullAddress = false
  }

  const phoneNumberUnformatted = contact?.phone

  const notificationOptInOriginalValue = contact?.notification_opt_in ?? ""
  let notificationOptIn: boolean
  if (notificationOptInOriginalValue !== "") {
    notificationOptIn = FormHelpers.str2bool(notificationOptInOriginalValue) || false
  } else {
    notificationOptIn = true
  }

  const showZipNotFoundMsg = false === (contact?.zip_found ?? "")

  return (
    <>
      <div className="form-group">
        <label className={requiredLabel} htmlFor={`contacts_${uuid}_first_name`}>First Name</label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][first_name]`}
            id={`contacts_${uuid}_first_name`}
            value={contact?.first_name ?? ""}
            onChange={onInputChange}
            className="form-control"
            placeholder="First Name"
            required={required}
            maxLength={100}
          />
        </div>
      </div>

      <div className="form-group">
        <label className={requiredLabel} htmlFor={`contacts_${uuid}_last_name`}>Last Name</label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][last_name]`}
            id={`contacts_${uuid}_last_name`}
            value={contact?.last_name ?? ""}
            onChange={onInputChange}
            className="form-control"
            placeholder="Last Name"
            required={required}
            maxLength={100}
          />
        </div>
      </div>

      <div className="form-group">
        <label className={requiredLabel} htmlFor={`contacts_${uuid}_email`}>
          Email
        </label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][email]`}
            id={`contacts_${uuid}_email`}
            value={contact?.email ?? ""}
            onChange={onInputChange}
            type="email"
            pattern={EMAIL_FIELD_PATTERN}
            className="form-control"
            placeholder="Email"
            data-fv-icon="false"
            data-fv-notempty="true"
            data-fv-notempty-message="Please enter a valid email address"
            data-fv-emailaddress="true"
            data-fv-emailaddress-message="Please enter a valid email address"
            data-fv-excluded-values={props.excluded_addresses.join(",")}
            data-fv-excluded-values-message={"Technical support contacts must be different"}
            required={required}
            maxLength={300}
          />
        </div>
      </div>
      {email_confirmation && (
        <div className="form-group">
          <label htmlFor={`contacts_${uuid}_email_confirmation`} className={requiredLabel}>
            Confirm Email
          </label>
          <div className={mainInputClass}>
            <input
              name={`contacts[${uuid}][email_confirmation]`}
              value={contact?.email_confirmation ?? ""}
              onChange={onInputChange}
              type="email"
              pattern={EMAIL_FIELD_PATTERN}
              className="form-control"
              id={`contacts_${uuid}_email_confirmation`}
              placeholder="Confirm Email"
              data-fv-icon="false"
              data-fv-matches={`contacts[${uuid}][email]`}
              data-fv-matches-message="Must match email address"
              required={required}
              maxLength={300}
            />
          </div>
        </div>
      )}
      <div className="form-group">
        <label className={requiredLabel} htmlFor={`contacts_${uuid}_phone`}>Phone</label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][phone]`}
            id={`contacts_${uuid}_phone`}
            value={formatPhoneNumber(phoneNumberUnformatted) || phoneNumberUnformatted || ""}
            onChange={onInputChange}
            data-fv-phone-message="Please enter a valid 10-digit phone number"
            className="form-control"
            placeholder="Phone"
            required={required}
            maxLength={14}
          />
        </div>
      </div>

      <div className="form-group">
        <label className={labelClass} htmlFor={`contacts_${uuid}_phone_extension`}>Extension</label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][phone_extension]`}
            id={`contacts_${uuid}_phone_extension`}
            value={contact?.phone_extension ?? ""}
            onChange={onInputChange}
            className="form-control"
            placeholder="Extension"
            maxLength={20}
          />
        </div>
      </div>

      <div className="form-group">
        <label className={requiredLabel} htmlFor={`contacts_${uuid}_company_name`}>Organization Name</label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][company_name]`}
            id={`contacts_${uuid}_company_name`}
            value={contact?.company_name ?? ""}
            onChange={onInputChange}
            className="form-control"
            placeholder="Organization Name"
            required={required}
            maxLength={100}
          />
        </div>
      </div>

      <div className="form-group">
        <label className={labelClass} htmlFor={`contacts_${uuid}_title`}>Job Title</label>
        <div className={mainInputClass}>
          <input
            name={`contacts[${uuid}][title]`}
            id={`contacts_${uuid}_title`}
            value={contact?.title ?? ""}
            onChange={onInputChange}
            className="form-control"
            placeholder="Job Title"
            maxLength={100}
          />
        </div>
      </div>

      <div className="form-group">
        <label className={requiredLabel} htmlFor={`contacts_${uuid}_street_1`}>Address</label>
        <div className={mainInputClass}>
          <div>
            <input
              name={`contacts[${uuid}][street_1]`}
              id={`contacts_${uuid}_street_1`}
              value={contact?.street_1 ?? ""}
              onChange={onInputChange}
              className="form-control"
              placeholder="Mailing Address"
              required={required}
              maxLength={100}
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="sr-only" htmlFor={`contacts_${uuid}_street_2`}>Mailing Address (Line 2 - Optional)</label>
        <div className={mainInputClass + " col-sm-offset-3"}>
          <input
            id={`contacts_${uuid}_street_2`}
            name={`contacts[${uuid}][street_2]`}
            value={contact?.street_2 ?? ""}
            onChange={onInputChange}
            className="form-control"
            placeholder="Mailing Address (Line 2 - Optional)"
            aria-label="Mailing Address (Line 2 - Optional)"
            maxLength={100}
          />
        </div>
      </div>

      <div>
        <div className="form-group">
          <div className={mainInputClass + " col-sm-offset-3"}>
            <div className="row">
              {shouldDisplayFullAddress && (
                <div className="col-sm-4">
                  <label className="sr-only" htmlFor={`contacts_${uuid}_city`}>City</label>
                  <input
                    name={`contacts[${uuid}][city]`}
                    id={`contacts_${uuid}_city`}
                    value={contact?.city ?? ""}
                    onChange={onInputChange}
                    className="form-control city"
                    placeholder="City"
                    aria-label="City"
                    required={required}
                    maxLength={100}
                  />
                </div>
              )}
              {shouldDisplayFullAddress && (
                <div className="col-sm-2">
                  <label className="sr-only" htmlFor={`contacts_${uuid}_state`}>State</label>
                  <input
                    name={`contacts[${uuid}][state]`}
                    id={`contacts_${uuid}_state`}
                    value={contact?.state ?? ""}
                    onChange={onInputChange}
                    className="form-control state"
                    placeholder="State"
                    aria-label="State"
                    required={required}
                    maxLength={100}
                  />
                </div>
              )}
              <div className="col-sm-4">
                <label className={showZipNotFoundMsg ? "has-error" : "required"} htmlFor={`contacts_${uuid}_zip`}>
                  <input
                    id={`contacts_${uuid}_zip`}
                    name={`contacts[${uuid}][zip]`}
                    value={contact?.zip ?? ""}
                    onChange={onInputChange}
                    onBlur={zipCodeBlurred}
                    className="form-control zip"
                    placeholder="ZIP Code"
                    data-fv-notempty-message="Please enter a valid ZIP code."
                    data-fv-zipcode-message="Please enter a valid ZIP code."
                    required={required}
                    maxLength={20}
                    style={{ marginBottom: "3px", fontWeight: "normal" }}
                  />
                  {showZipNotFoundMsg ? <span className="help-block">ZIP Code Not Found. *</span> : "ZIP Code"}
                </label>
              </div>
              {shouldDisplayFullAddress && (
                <div className="col-sm-4">
                  <label className="sr-only" htmlFor={`contacts_${uuid}_country_code`}>Country</label>
                  <select
                    id={`contacts_${uuid}_country_code`}
                    name={`contacts[${uuid}][country_code]`}
                    value={contact?.country_code ?? ""}
                    onChange={onInputChange}
                    className="form-control country"
                    aria-label="Country"
                    required={required}
                  >
                    <option value="">Country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="form-group required"
        role="radiogroup"
        aria-labelledby={`contacts_${uuid}_notifications_label`}
      >
        <label className={requiredLabel} id={`contacts_${uuid}_notifications_label`}>
          Email Notifications
        </label>
        <div className="radio radio-yes col-sm-2">
          <label>
            <input
              name={`contacts[${uuid}][notification_opt_in]`}
              value={YES}
              checked={notificationOptIn}
              onChange={onInputChange}
              className="qualifier-radio"
              required={true}
              data-fv-notempty={true}
              data-fv-ignore-error-message={true}
              data-fv-notempty-message=""
              type="radio"
            />
            Yes
          </label>
        </div>
        <div className="radio radio-no col-sm-2">
          <label htmlFor={`contacts_${uuid}_notification_opt_in_no`}>
            <input
              id={`contacts_${uuid}_notification_opt_in_no`}
              name={`contacts[${uuid}][notification_opt_in]`}
              value={NO}
              checked={!notificationOptIn}
              onChange={onInputChange}
              className="qualifier-radio"
              required={true}
              data-fv-notempty={true}
              data-fv-ignore-error-message={true}
              data-fv-notempty-message=""
              type="radio"
            />
            No
          </label>
        </div>
      </div>
    </>
  )
}

interface IPlanMultiSelectProps extends ICoreProps {
  name: keyof IRegistrationForm
  selectablePlans: IPlanParentOrganization[]
  showNotApplicable: boolean
  oneRequiredPlanGroup?: OneRequiredInGroupProps
}

function PlanMultiSelect(props: IPlanMultiSelectProps): React.ReactNode {
  const {
    form,
    name,
    selectablePlans,
    showNotApplicable,
    onInputChange,
    setFormValue,
  } = props

  const contactRef: IContactRef = ((form || {})[ name ] || {}) as IContactRef

  const addBlankPlan = useCallback(() => {
    const planCodes = contactRef.plan_codes || []
    planCodes.push({code: ""})
    setFormValue([name as string, "plan_codes"], planCodes)
  }, [contactRef.plan_codes, name, setFormValue])

  const removePlan = useCallback((index: number) => {
    const planCodes = contactRef.plan_codes
    if (!planCodes) {
      return
    }

    planCodes.splice(index, 1)

    setFormValue([name as string, "plan_codes"], planCodes)
  }, [contactRef.plan_codes, name, setFormValue])

  const setPlanCode = useCallback((event: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value, path } = FormHelpers.getFormChangeArgsFromEvent(event)

    // if ( value === NOT_APPLICABLE ) {
    //   // pop off "code" and index, and clear the entire array
    //   path.pop()
    //   path.pop()
    //   this.props.setFormValue(path, [{code: value}])
    //   return
    // }

    setFormValue(path, value)

    // TODO: also set contact to N/A for good UX
    // path.pop() // code
    // path.pop() // index
    // path.pop() // plan_codes
    // path.push("contact")
    // this.props.setFormValue(path, "not-applicable")
  }, [setFormValue])

  const planCodes = contactRef.plan_codes || [{code: ""}]
  const selectedCodes = planCodes.map((p) => p.code)

  // if (name === "medical_claims_submission") {
  //   console.log("planCodes", planCodes)
  // }

  const notApplicableSelected = planCodes.length >= 1 && !planCodes.find(pc => pc.code !== NOT_APPLICABLE)

  const excludedCodes = new Set<string>()

  return (
    <>
      {selectedCodes.map((planCode, index) => {
        const id = `${name}_plan_${index}`

        if (index > 0) {
          const previous = selectedCodes[index - 1]
          if (previous !== OTHER && previous !== undefined) {
            excludedCodes.add(previous)
          }
        }

        const options = selectablePlans.filter(p => !excludedCodes.has(p.planCode))

        return (
          <div key={id}>
            <div className="form-group">
              <label className="col-sm-3 control-label required" htmlFor={id}>Plan</label>
              <div className="col-sm-9">
                <select
                  id={id}
                  name={`${name}[plan_codes][${index}][code]`}
                  className="form-control contact-select"
                  onChange={setPlanCode}
                  value={planCode}
                  data-fv-notempty-message="Select a plan"
                  data-fv-onerequiredplaningroup={index === 0 ? props.oneRequiredPlanGroup : undefined}
                  data-fv-onerequiredplaningroup-message={props.oneRequiredPlanGroup ? (props.oneRequiredPlanGroup.message || "At least one plan must be selected") : undefined}
                  required={true}
                >
                  <option value=""/>
                  {options.map(plan => {
                    return (
                      <option key={plan.planCode} value={plan.planCode}>
                        {plan.planName} ({plan.planCode})
                      </option>
                    )
                  })}
                  <option value={OTHER}>Other (Please Specify)</option>
                  {showNotApplicable && <option value={NOT_APPLICABLE}>Not Applicable</option>}
                </select>

              </div>
            </div>
            {planCode === OTHER && (
              <div className="form-group">
                <div className="col-sm-9 col-sm-offset-3">
                  <label className="sr-only" htmlFor={`${name}_plan_codes_${index}_other_name`}>Specify Plan Name</label>
                  <input
                    name={`${name}[plan_codes][${index}][other_name]`}
                    id={`${name}_plan_codes_${index}_other_name`}
                    onChange={onInputChange}
                    value={contactRef.plan_codes?.[index]?.other_name ?? ""}
                    className="form-control"
                    required={true}
                    placeholder="Specify Plan Name"
                    aria-label="Specify Plan Name"
                    maxLength={100}
                  />
                </div>
              </div>
            )}

            {index > 0 && planCodes.length > 1 && !notApplicableSelected && (
              <div
                className="col-sm-9 col-sm-offset-3"
                onClick={() => removePlan(index)}
                style={{marginBottom: "10px"}}
              >
                <p className="add-another">
                  <span className="fa fa-minus-circle fa-lg text-danger remove-this" />
                    &nbsp;&nbsp;Remove This Plan
                </p>
              </div>
            )}
          </div>
        )
      })}

      {!notApplicableSelected && (
        <div className="form-group" onClick={addBlankPlan}>
          <div className="col-sm-8 col-sm-offset-3">
            <p className="add-another" style={{marginLeft: "5px"}}>
              <i className="fa fa-plus-circle fa-lg text-success add-another" />
                &nbsp;&nbsp;Add Another Plan
            </p>
          </div>
          <div className="col-sm-12">
            <hr style={{ margin: "5px 0" }} />
          </div>
        </div>
      )}
    </>
  )
}
